import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Clock, ShieldCheck, LineChart, Users, FileText } from "lucide-react";
import { STUDENTS_IMG } from "@/lib/constants";

const benefits = [
  { icon: Clock, title: "Under 20 minutes", body: "A single web-based session gives clarity before test prep begins." },
  { icon: LineChart, title: "Science-backed", body: "Learning psychology, neuroscience and 25+ years of classroom insight." },
  { icon: Users, title: "Cohort-ready", body: "Deploy to individual students, a grade level or the entire junior class." },
  { icon: ShieldCheck, title: "Data-safe", body: "Approved by IT at a highly selective private school. Only first name, email and grad date collected." },
  { icon: FileText, title: "Actionable reports", body: "Students receive a personalized study roadmap and recommendation." },
  { icon: Check, title: "Zero integration burden", body: "No complex SIS integration. Web browser. Unique code per student." },
];

const audienceValue = [
  {
    who: "For School Leaders",
    points: [
      "Boost schoolwide entrance-exam scores and ratings",
      "Reduce wasted resources on the wrong prep pathway",
      "Deliver defensible, data-backed test recommendations",
      "Cohort report with focused strategy for your junior class",
    ],
  },
  {
    who: "For Counselors",
    points: [
      "Clear starting point for every student advising session",
      "Confident, personalized guidance for families",
      "Fewer repetitive questions from parents",
      "Prioritize your time where it moves the needle",
    ],
  },
  {
    who: "For Students & Families",
    points: [
      "Walk into test day knowing they're in the right room",
      "Personalized study roadmap based on graduation year",
      "Digital copy of Dr. Gifford's College Prep Success Blueprint",
      "Less stress, more confidence, better scores",
    ],
  },
];

const faqs = [
  { q: "What is TestFit®?", a: "A research-backed diagnostic that determines whether the SAT or ACT is a better match for a student's strengths, pacing and college goals. Completed in under 20 minutes." },
  { q: "How is it different from a practice test?", a: "TestFit® is a decision tool, not a practice test. Its purpose is to answer the question that should come before preparation begins: Which test should this student prepare to take?" },
  { q: "How do schools implement it?", a: "Purchase seat licenses for individual students, a cohort or your entire junior class. Students receive a unique code and access via any web browser. No SIS integration required." },
  { q: "How is student data protected?", a: "Data was reviewed and approved by the IT department of a highly selective private school in Miami. We collect only first name, email and graduation date. Student data is never fed to public AI systems." },
];

export default function TestFit() {
  return (
    <div data-testid="page-testfit">
      <section className="relative bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-24 md:pt-32 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline text-[#0F4C5C]">Student Decision Intelligence</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mt-4">
              The question that should come <em className="italic text-[#0F4C5C]">before</em> test prep begins.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#22223B]/80 leading-relaxed max-w-xl">
              Stop preparing students for the wrong test. TestFit® tells you — in under 20 minutes — whether the SAT or ACT is a better match for each student's strengths.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact?interest=testfit_demo" data-testid="testfit-cta-demo" className="btn-primary">
                Schedule a School Demo <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact?interest=testfit_pricing" className="btn-secondary">
                Request School Pricing
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img src={STUDENTS_IMG} alt="Students preparing for SAT and ACT" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <h2 className="md:col-span-7 font-serif-display text-4xl md:text-5xl leading-tight">
              Why <em className="italic text-[#0F4C5C]">TestFit®</em> works where others don't.
            </h2>
            <p className="md:col-span-4 md:col-start-9 text-[#22223B]/75 leading-relaxed">
              There is no other diagnostic on the market using education research and neuroscience to match each student's unique strengths with each test's design format.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="card-soft p-8">
                <b.icon size={26} strokeWidth={1.4} className="text-[#0F4C5C]" />
                <h3 className="font-serif-display text-xl mt-5">{b.title}</h3>
                <p className="mt-3 text-[15px] text-[#22223B]/75 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#0F4C5C] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <div className="overline text-[#E3B23C]">Value by audience</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight max-w-3xl">
            Personalization begins with the <em className="italic text-[#E3B23C]">right decision.</em>
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {audienceValue.map((a) => (
              <div key={a.who} className="p-8 border border-white/10 rounded-lg bg-white/[0.03]">
                <div className="overline text-[#E3B23C]">{a.who}</div>
                <ul className="mt-6 space-y-3">
                  {a.points.map((p) => (
                    <li key={p} className="flex gap-3 text-[15px] text-[#FDFBF7]/85">
                      <Check size={18} strokeWidth={1.6} className="text-[#E3B23C] shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="overline">Frequently asked</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">Answers, before you ask.</h2>
          <div className="mt-12 divide-y divide-[#0F4C5C]/15">
            {faqs.map((f, i) => (
              <details key={i} data-testid={`testfit-faq-${i}`} className="group py-6">
                <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                  <span className="font-serif-display text-xl md:text-2xl leading-snug">{f.q}</span>
                  <span className="text-[#0F4C5C] text-2xl leading-none mt-1 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#22223B]/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="overline">Ready when you are</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
            Bring TestFit® to your students <em className="italic text-[#0F4C5C]">this semester.</em>
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact?interest=testfit_demo" data-testid="testfit-bottom-cta-demo" className="btn-primary">
              Schedule a Demonstration <ArrowUpRight size={16} />
            </Link>
            <a href="https://testfit.org" target="_blank" rel="noreferrer" className="btn-secondary">
              Visit testfit.org <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
